import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Phone, Eye, EyeSlash } from "@phosphor-icons/react";

export default function Signup() {
  const { signup, user, ready } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", company: "", email: "", password: "" });
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (ready && user) navigate("/dashboard", { replace: true });
  }, [ready, user, navigate]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    setLoading(true);
    try {
      await signup({
        name: form.name.trim(),
        company: form.company.trim(),
        email: form.email.trim(),
        password: form.password,
      });
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Brand panel */}
      <div className="hidden lg:flex flex-col justify-between p-12 bg-primary text-primary-foreground relative overflow-hidden">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-white/10 blur-3xl" />
        <div className="absolute bottom-0 -left-16 w-80 h-80 rounded-full bg-black/10 blur-3xl" />
        <div className="flex items-center gap-3 relative">
          <div className="w-11 h-11 rounded-xl bg-white/15 grid place-items-center">
            <Phone className="w-6 h-6" weight="fill" />
          </div>
          <span className="font-display font-bold text-xl tracking-tight">WA Calls</span>
        </div>
        <div className="relative">
          <h1 className="text-4xl font-display font-bold leading-tight tracking-tight">
            Launch your first AI voice campaign in minutes.
          </h1>
          <p className="mt-4 text-primary-foreground/80 max-w-md">
            Create a customer account, add cash, buy concurrent channels and start
            running human-like outbound conversations at scale — all on your own SIP
            infrastructure.
          </p>
        </div>
        <p className="text-xs text-primary-foreground/60 relative">© 2026 WA Calls · Control Plane</p>
      </div>

      {/* Form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-xl bg-primary text-primary-foreground grid place-items-center">
              <Phone className="w-5 h-5" weight="fill" />
            </div>
            <span className="font-display font-bold text-lg">WA Calls</span>
          </div>
          <h2 className="text-2xl font-display font-semibold tracking-tight">Create your account</h2>
          <p className="text-sm text-muted-foreground mt-1.5">Get started with WA Calls — no card required.</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Full name</Label>
              <Input id="name" value={form.name} required onChange={set("name")}
                data-testid="signup-name" placeholder="Jane Doe" className="h-11" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="company">Company</Label>
              <Input id="company" value={form.company} required onChange={set("company")}
                data-testid="signup-company" placeholder="Acme Inc." className="h-11" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={form.email} required onChange={set("email")}
                data-testid="signup-email" placeholder="you@company.com" className="h-11" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input id="password" type={show ? "text" : "password"} value={form.password} required
                  onChange={set("password")} data-testid="signup-password"
                  placeholder="At least 8 characters" className="h-11 pr-10" />
                <button type="button" onClick={() => setShow((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                  {show ? <EyeSlash className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            {error && (
              <p className="text-sm text-destructive" data-testid="signup-error">{error}</p>
            )}
            <Button type="submit" disabled={loading} data-testid="signup-submit"
              className="w-full h-11 rounded-lg font-medium">
              {loading ? "Creating account…" : "Create account"}
            </Button>
          </form>

          <p className="text-sm text-muted-foreground mt-6 text-center">
            Already have an account?{" "}
            <Link to="/login" data-testid="signup-to-login" className="text-primary font-medium hover:underline">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
